import { useLayoutEffect, type RefObject } from 'react';

/**
 * Lays the children of a CSS grid out as a masonry: the children go into the columns in turn, left to right in every
 * row (with three columns the fourth child goes to the first column), and each spans as many of the grid's (tiny) rows
 * as its own height plus the gap, so it starts right under the child above it instead of under the tallest child of
 * the row. The grid keeps its own columns; it needs `grid-auto-rows` set to the row unit (1px), no row gap (the column
 * gap stands in for the vertical gap too) and `grid-auto-flow: dense`, so a child takes the first free rows of its
 * column.
 * @param gridRef - The grid element whose children are laid out
 * @param childrenKey - Changes whenever the children do (for example their keys joined), so a child added, removed or
 *   replaced is measured and watched
 */
export function useMasonryGrid(gridRef: RefObject<HTMLElement | null>, childrenKey: string): void {
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof window.ResizeObserver === 'undefined') {
      return undefined;
    }

    const items = Array.from(grid.children) as HTMLElement[];
    const layOut = () => {
      const style = window.getComputedStyle(grid);
      const rowUnit = parseFloat(style.gridAutoRows) || 1;
      const gap = parseFloat(style.columnGap) || 0;
      const columnCount = Math.max(1, style.gridTemplateColumns.split(' ').filter(Boolean).length);
      items.forEach((item, index) => {
        const rows = Math.max(1, Math.ceil((item.getBoundingClientRect().height + gap) / rowUnit));
        item.style.gridColumnStart = String((index % columnCount) + 1);
        item.style.gridRowEnd = `span ${rows}`;
      });
    };

    // A child's size changes when its data loads, its dimension is switched or the screen fits another number of columns.
    const observer = new window.ResizeObserver(layOut);
    items.forEach((item) => observer.observe(item));
    layOut();

    return () => observer.disconnect();
  }, [gridRef, childrenKey]);
}
